import { useEffect, useRef } from 'react';

const overflowingDialog = () => {
  const nodes = document.querySelectorAll('.ae-dialog-scroll');
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    const el = nodes[i];
    if (el.scrollHeight > el.clientHeight + 1) return el;
  }
  return null;
};

const placeThumb = (el, thumb) => {
  const max = el.scrollHeight - el.clientHeight;
  const thumbH = Math.max(48, (el.clientHeight / el.scrollHeight) * window.innerHeight);
  const travel = Math.max(1, window.innerHeight - thumbH);
  const top = max <= 0 ? 0 : (el.scrollTop / max) * travel;
  thumb.style.height = `${thumbH}px`;
  thumb.style.transform = `translateY(${top}px)`;
  return { max, thumbH, travel };
};

const DialogScrollRail = () => {
  const railRef = useRef(null);
  const thumbRef = useRef(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    const rail = railRef.current;
    const thumb = thumbRef.current;
    if (!rail || !thumb) return undefined;

    let frame = 0;
    const update = () => {
      const el = overflowingDialog();
      scrollerRef.current = el;
      if (!el) {
        rail.hidden = true;
        return;
      }
      rail.hidden = false;
      placeThumb(el, thumb);
    };

    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    const onScroll = (event) => {
      if (event.target?.classList?.contains('ae-dialog-scroll')) schedule();
    };

    const onPointerDown = (event) => {
      const el = scrollerRef.current;
      if (!el || event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      const startY = event.clientY;
      const startScroll = el.scrollTop;
      const { max, travel } = placeThumb(el, thumb);
      const move = (ev) => {
        const next = startScroll + ((ev.clientY - startY) / travel) * max;
        el.scrollTop = Math.min(max, Math.max(0, next));
        placeThumb(el, thumb);
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    };

    const onWheel = (event) => {
      const el = scrollerRef.current;
      if (!el) return;
      el.scrollTop += event.deltaY;
      placeThumb(el, thumb);
      event.preventDefault();
    };

    schedule();
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', schedule);
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    rail.addEventListener('pointerdown', onPointerDown);
    rail.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
      rail.removeEventListener('pointerdown', onPointerDown);
      rail.removeEventListener('wheel', onWheel);
    };
  }, []);

  return (
    <div ref={railRef} className="ae-dialog-rail" hidden aria-hidden="true">
      <div ref={thumbRef} className="ae-dialog-rail-thumb" />
    </div>
  );
};

export default DialogScrollRail;
